import { ButtonBase, Fade, IconButton, Typography } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Remove'
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat'
import { useEffect, useRef } from 'react'

import { getTranslate, lang } from '../../localization'
import { DateRangePicker } from '../DatePicker/DateRangePicker'
import { DateRangePickerProps } from '../DatePicker/DateRangePickerComponents/types/DateRangePicker.types'
import useStyles from './styles/index.styles'

interface TimePeriodFormProps extends DateRangePickerProps {
  isOpen: boolean
  toggleDatePicker?: () => void
  onDoneClickForCloseDatePicker?: boolean
  setOnDoneClickForCloseDatePicker?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  setTimePickerSelected?: React.Dispatch<React.SetStateAction<boolean>>
  deletable?: boolean
  onDelete?: () => void
  endDateCanBeSelectAsStartDate?: boolean
  showDaysInFooter?: boolean
  onSelectedWeekDays?: (weekdays: string[]) => void
  selectedWeekDays?: string[]
  left?: boolean
  lightVersion?: boolean
}

const TimePeriodForm: React.FC<TimePeriodFormProps> = ({
  isOpen,
  toggleDatePicker,
  onDoneClickForCloseDatePicker,
  setOnDoneClickForCloseDatePicker,
  setTimePickerSelected,
  deletable,
  onDelete,
  endDateCanBeSelectAsStartDate = true,
  showDaysInFooter = false,
  onSelectedWeekDays,
  selectedWeekDays,
  range = true,
  left,
  lightVersion,
  ...otherprops
}) => {
  const { dateObject } = otherprops
  const classes = useStyles()
  const datePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setTimeout(() => {
          toggleDatePicker?.()
        }, 270)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, isOpen, onDoneClickForCloseDatePicker])

  return (
    <div
      ref={datePickerRef}
      className={[classes.root, isOpen ? classes.rootActive : ''].join(' ')}>
      {/*
        aria-disabled={true} was hardcoded here. The container advertised itself as
        permanently disabled while wrapping an operable ButtonBase, so screen-reader users
        skipped the only way to set a date range — and the announcement was false in both
        directions: the control works, and it said it did not.

        Shared component: this also affects Booking List's Stay Dates.
      */}
      {deletable && (
        <IconButton onClick={onDelete} className={classes.deleteIconContainer}>
          <DeleteIcon className={classes.deleteIcon} />
        </IconButton>
      )}
      <ButtonBase
        onClick={() => {
          setTimePickerSelected?.(!isOpen)
          toggleDatePicker?.()
        }}
        className={[
          classes.timePeriodInputContainer,
          lightVersion ? classes.timePeriodInputContainerLight : ''
        ].join(' ')}>
        {range ? (
          lightVersion ? (
            <>
              <div className={classes.timePeriodInputItemLight}>
                {!dateObject?.gregorianStartDate ? (
                  <Typography className={classes.timeInputLabelLight}>
                    {getTranslate('From')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      className={[
                        classes.timeInputValueLight,
                        showDaysInFooter &&
                          classes.timeInputValueForRateConnection,
                        !dateObject?.gregorianStartDate
                          ? classes.timeInputValueInvisible
                          : ''
                      ].join(' ')}>
                      {lang === 'fa'
                        ? dateObject?.jalaaliStartDate
                        : dateObject?.gregorianStartDate}
                    </Typography>
                  </>
                )}
              </div>
              <TrendingFlatIcon
                className={[
                  classes.trendingIcon,
                  lang === 'fa' ? classes.trendingIconRtl : ''
                ].join(' ')}
              />
              <div className={classes.timePeriodInputItemLight}>
                {!dateObject?.gregorianStartDate ? (
                  <Typography className={classes.timeInputLabelLight}>
                    {getTranslate('To')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      className={[
                        classes.timeInputValueLight,
                        showDaysInFooter &&
                          classes.timeInputValueForRateConnection,
                        !dateObject?.gregorianEndDate
                          ? classes.timeInputValueInvisible
                          : ''
                      ].join(' ')}>
                      {lang === 'fa'
                        ? dateObject?.jalaaliEndDate
                        : dateObject?.gregorianEndDate}
                    </Typography>
                  </>
                )}

                {selectedWeekDays && selectedWeekDays?.length > 0 && (
                  <span className={classes.daysContainerHide}></span>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={classes.timePeriodInputItem}>
                {(!showDaysInFooter || !dateObject?.gregorianStartDate) && (
                  <Typography className={classes.timeInputLabel}>
                    {getTranslate('From')}
                  </Typography>
                )}
                <Typography
                  className={[
                    classes.timeInputValue,
                    showDaysInFooter && classes.timeInputValueForRateConnection,
                    !dateObject?.gregorianStartDate
                      ? classes.timeInputValueInvisible
                      : ''
                  ].join(' ')}>
                  {lang === 'fa'
                    ? dateObject?.jalaaliStartDate
                    : dateObject?.gregorianStartDate}
                </Typography>
                {showDaysInFooter && (
                  <div className={classes.daysContainer}>
                    {selectedWeekDays?.map((weekDay, index) => (
                      <Typography key={index}>
                        {getTranslate(`${weekDay.slice(0, 3)}`)}
                        {selectedWeekDays.length - 1 === index ? '' : `,`}&nbsp;
                      </Typography>
                    ))}
                  </div>
                )}
              </div>
              <div className={classes.timePeriodInputItem}>
                {(!showDaysInFooter || !dateObject?.gregorianEndDate) && (
                  <Typography className={classes.timeInputLabel}>
                    {getTranslate('To')}
                  </Typography>
                )}
                <Typography
                  className={[
                    classes.timeInputValue,
                    showDaysInFooter && classes.timeInputValueForRateConnection,
                    !dateObject?.gregorianEndDate
                      ? classes.timeInputValueInvisible
                      : ''
                  ].join(' ')}>
                  {lang === 'fa'
                    ? dateObject?.jalaaliEndDate
                    : dateObject?.gregorianEndDate}
                </Typography>
                {selectedWeekDays && selectedWeekDays?.length > 0 && (
                  <span className={classes.daysContainerHide}></span>
                )}
              </div>
            </>
          )
        ) : (
          <Typography
            className={[
              classes.timeInputValue,
              !dateObject?.gregorianStartDate
                ? classes.timeInputValueInvisible
                : ''
            ].join(' ')}>
            {lang === 'fa'
              ? dateObject?.jalaaliStartDate
              : dateObject?.gregorianStartDate}
          </Typography>
        )}
      </ButtonBase>
      {isOpen && (
        <Fade in={isOpen}>
          <div
            className={
              !left
                ? classes.datePickerContainer
                : classes.datePickerContainerLeft
            }>
            <DateRangePicker
              {...otherprops}
              onDoneClicked={() => {
                setTimePickerSelected?.(false)
                setOnDoneClickForCloseDatePicker?.(false)
              }}
              type={lang === 'fa' ? 'jalaali' : 'gregorian'}
              endDateCanBeSelectAsStartDate={endDateCanBeSelectAsStartDate}
              showDaysInFooter={showDaysInFooter}
              onSelectedWeekDays={onSelectedWeekDays}
              selectedWeekDays={selectedWeekDays}
            />
          </div>
        </Fade>
      )}
    </div>
  )
}

export default TimePeriodForm
